/**
 * Mobile-only bottom sheet for the ballot view. Mirrors the architecture
 * of ``votemate-mobile-app/src/providers/BallotSheetProvider.tsx`` —
 * three detents (closed / mid / full), drag-to-resize, snap-to-nearest
 * on release with velocity-based fling, backdrop dims the content
 * beneath as the sheet rises.
 *
 * Why not MUI's ``Drawer`` or a library like ``vaul``: both rely on
 * React portals, and portal-based primitives don't reliably paint on
 * this app's mobile breakpoint (see ``feedback_mui_portal_mobile_paint``
 * in the workspace memory). The sheet, grabber, and backdrop render
 * inline — same workaround the mobile "Ballot not finalized" chip uses.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAuth } from '@/components/auth/AuthProvider';
import useAnalytics from '@/hooks/useAnalytics';

export type BallotSheetDetent = 'closed' | 'mid' | 'full';

/**
 * Geometry of the chat input that the sheet should anchor to when
 * closed. ``top`` / ``left`` / ``width`` are in CSS px relative to
 * the viewport (i.e., the values you'd read out of
 * ``getBoundingClientRect``). ``null`` means no anchor — the sheet
 * sits at the viewport bottom in full width (the default).
 */
export interface ChatInputRect {
  top: number;
  left: number;
  width: number;
}

// Default detent fractions. The closed fraction can be overridden
// per-render via ``minClosedFrac`` so callers (e.g., guests on a new
// chat page with no address) can force the sheet to rest at a small
// non-zero height instead of fully closing — exposing the address
// input inside the white area without showing the rest of the ballot.
const DEFAULT_CLOSED_FRAC = 0;
// ``PREVIEW_CLOSED_FRAC`` lifts the closed detent so the ballot's
// white-area address row peeks above the teal header — used for
// guests on the new chat page with no saved address. We add
// ``env(safe-area-inset-bottom) / fullHeight`` at runtime so the
// preview view stays the intended height on devices with a home
// indicator (otherwise the sheet's safe-area shift trims the
// visible portion).
const PREVIEW_CLOSED_FRAC = 0.11;
const DETENT_FRACS_BASE: Record<BallotSheetDetent, number> = {
  closed: DEFAULT_CLOSED_FRAC,
  // Half-open rests at 50% of the max height (parity with the mobile app).
  mid: 0.5,
  full: 1,
};

/** Rendered height of the app bar's toolbar. Consumers that hide the app bar
 * subtract this from ``headerOffsetPx`` to keep the rest of the offset (the
 * notification banner) intact. */
export const MOBILE_TOOLBAR_HEIGHT_PX = 48;

// Default reserved space at the top of the viewport for the app
// header — matches the rendered toolbar height (48px). Callers can
// override via the ``headerOffsetPx`` prop to account for additional
// chrome above the toolbar (notification banner, safe-area inset).
const DEFAULT_HEADER_OFFSET_PX = MOBILE_TOOLBAR_HEIGHT_PX;

// Velocity threshold for fling (pixels per millisecond). 0.5 px/ms =
// 500 px/sec — matches the mobile app's PanResponder threshold.
// On fling, the sheet steps ONE detent in the gesture's direction
// (e.g., full → mid, not full → closed). Crossing two detents at
// once requires either a deliberate drag or two flings.
const FLING_VELOCITY_THRESHOLD = 0.5;

// Snap thresholds — fraction of the distance to the *next* detent
// (in the drag's direction) that the user must cross to commit to it.
// Symmetric 25% both ways: easy to step to the next detent in either
// direction once you've moved a quarter of the way there.
//   closed → mid:  0 + 0.5 * 0.25 = 0.125
//   mid    → full: 0.5 + 0.5 * 0.25 = 0.625
//   full   → mid:  1.0 − 0.5 * 0.25 = 0.875
//   mid    → closed: 0.5 − 0.5 * 0.25 = 0.375
const SNAP_UP_THRESHOLD_FRAC = 0.25;
const SNAP_DOWN_THRESHOLD_FRAC = 0.25;

const DETENT_ORDER: ReadonlyArray<BallotSheetDetent> = ['closed', 'mid', 'full'];

function adjacentDetent(current: BallotSheetDetent, direction: 'up' | 'down'): BallotSheetDetent {
  const idx = DETENT_ORDER.indexOf(current);
  if (direction === 'up') {
    return idx < DETENT_ORDER.length - 1 ? DETENT_ORDER[idx + 1] : current;
  }
  return idx > 0 ? DETENT_ORDER[idx - 1] : current;
}

export type BallotSheetMode = 'personal' | 'national';

/** The three screens the mobile bottom tab bar switches between (tab-bar nav
 * mode). Ignored in sheet nav mode, where the draggable sheet is used instead. */
export type MobileBallotTab = 'chat' | 'ballot' | 'info';

interface BallotSheetContextValue {
  detent: BallotSheetDetent;
  /** Data-source mode for ``SampleBallotView``. Set by the wrapping
   * layout (which knows the user's auth / setup state) so both the
   * page-level ``BallotSheet`` and the in-chat one use the same
   * source. */
  ballotMode: BallotSheetMode;
  /** Locality label (e.g. "New York County, NY") the ballot is scoped
   * to. Set by ``SampleBallotView``; rendered centered under the title
   * in the sheet/grabber header so it stays visible when collapsed. */
  locality: string | null;
  /** Imperative setter for ``locality``. */
  setLocality: (value: string | null) => void;
  /** Which mobile screen the bottom tab bar is showing (tab-bar nav mode).
   * Ignored in sheet nav mode. */
  activeTab: MobileBallotTab;
  /** Switch the active mobile tab. */
  setActiveTab: (tab: MobileBallotTab) => void;
  /** True while the guest landing's address entry owns the whole phone screen
   * (tab nav mode, no address anywhere yet, on a ballot tab). The app bar and
   * the tab bar step aside for it — the landing carries its own sign-in and
   * language controls — so it reads as the standalone landing page it is. */
  landingFullScreen: boolean;
  /** Fraction (0..1) of the *resting* detent — changes only on settle, not per-frame. */
  restingFrac: number;
  /** Viewport Y (px) of the sheet's top edge (grabber) at the settled detent. */
  restingSheetTopPx: number;
  /** True while a drag gesture is in progress. */
  isDragging: boolean;
  /** Max sheet height in CSS px (full detent height) — viewport
   * height minus the header carve-out. The sheet's *closed* visible
   * height is just the teal header band; everything in between is
   * driven by ``heightFrac``. */
  maxHeightPx: number;
  /** Viewport height in CSS px (visualViewport-aware). Exposed so
   * sheet/backdrop can compute their anchors without re-measuring. */
  viewportHeightPx: number;
  /** Pixels of viewport reserved at the top for the app header. The
   * sheet at full detent stops at this Y; the backdrop starts here. */
  headerOffsetPx: number;
  /** Rect of the chat input the sheet should anchor to when closed,
   * or ``null`` for the default behavior (sheet sits flush at the
   * viewport bottom in full width). At the closed detent the sheet's
   * teal header lands flush on top of this rect, matching its width;
   * at the full detent the sheet covers the input. */
  chatInputRect: ChatInputRect | null;
  /** Imperative setter for ``chatInputRect``. The active chat input
   * registers its rect here; cleanup should pass ``null``. */
  setChatInputRect: (rect: ChatInputRect | null) => void;
  /** ``true`` when the on-screen keyboard is open (i.e.,
   * ``visualViewport.height`` is meaningfully smaller than
   * ``window.innerHeight``). Lets consumers like ``BallotSheet`` swap
   * the header height: compact (52px) when stacking above the
   * keyboard, extended (75px) when there's room for home-bar
   * clearance at the screen bottom. */
  keyboardUp: boolean;
  /** Pixels the on-screen keyboard occupies at the bottom of the
   * layout viewport (``window.innerHeight - visualViewport.height``).
   * Zero when no keyboard. Used as the sheet's ``bottom`` offset on
   * the landing page when the keyboard is up — without it the sheet
   * extends behind the keyboard because CSS ``bottom`` is relative
   * to the layout viewport (which doesn't shrink for the keyboard on
   * Chrome's default ``resizes-visual``) while our top calc uses the
   * visual viewport. */
  keyboardInsetPx: number;
  /** Open to the mid detent. No-op if already open. */
  open: () => void;
  /** Close to fully hidden. */
  close: () => void;
  /** Report the status popover's open state so the header can tell a dismiss
   * tap from an open-the-sheet tap. */
  setStatusPopoverOpen: (isOpen: boolean) => void;
  /** True while the status popover is open or was just dismissed. */
  isStatusPopoverActive: () => boolean;
  /** Snap directly to a specific detent. Pass ``{ instant: true }``
   * to suppress the 280ms transition for one frame — useful when the
   * snap is triggered by user input that's about to open the keyboard
   * (Safari iOS computes its auto-scroll-into-view target from the
   * input's current screen position, so a transition-in-progress
   * causes Safari to scroll the page based on the pre-snap position). */
  snapTo: (detent: BallotSheetDetent, opts?: { instant?: boolean }) => void;
  /** Set by ``snapTo({ instant: true })`` so the BallotSheet can
   * suppress its top/bottom transition for the next render. Cleared
   * automatically on the following frame. */
  pendingInstantSnap: boolean;
  /** Lift the ``closed`` detent off zero so the sheet rests at a
   * small visible band even when "closed". Used to surface the
   * address-entry input for guests on the new chat page who haven't
   * saved an address. Call ``setPreviewClosed(true)`` when the
   * scenario applies and ``setPreviewClosed(false)`` to revert. */
  setPreviewClosed: (enabled: boolean) => void;
  /** Desired visible height of the sheet at the preview-closed rest
   * state, in CSS px (measured from the top of the teal header band
   * down to the bottom of the address-input row in
   * ``SampleBallotView``). When set, the provider derives the
   * preview frac from this measurement so the address row sits
   * exactly on the sheet's bottom edge regardless of device size or
   * safe-area inset. ``null`` falls back to the static
   * ``PREVIEW_CLOSED_FRAC`` constant. */
  setPreviewTargetHeightPx: (heightPx: number | null) => void;
  /** Effective fraction of the closed detent (0 normally, or the
   * preview-rest fraction when ``previewClosed`` is on, plus any
   * safe-area-inset contribution). Exposed so consumers like the
   * BallotBackdrop can use it as the threshold below which their
   * effect (e.g., dimming opacity) stays at zero. */
  closedDetentFrac: number;
  /** Lets a child component register a callback that focuses the
   * MobileAddressBar's input. Called by ``MobileAddressBar`` on
   * mount; cleared (pass ``null``) on unmount. Stored as a ref so
   * registration doesn't re-render every consumer. */
  registerAddressInputFocuser: (fn: (() => void) | null) => void;
  /** Imperatively focuses the MobileAddressBar's input — used by the
   * LeftPaneMenu's "My Ballot Preview" item in the guest no-address
   * scenario, where there's no sheet to open. No-op if the bar isn't
   * mounted. */
  focusAddressInput: () => void;
  /** Pixels reserved at the bottom of the viewport for the device's
   * home indicator / safe area (read at runtime from
   * ``env(safe-area-inset-bottom)``). Consumers shift their bottom-
   * anchored geometry up by this amount so they land at the top of
   * the SafeAreaFiller instead of behind it. */
  safeAreaBottomPx: number;
  /** Drive the live drag fraction directly (clamped 0..1). Use during
   * an imperative gesture handler; ``settleSheet`` ends it. */
  updateDragFrac: (frac: number) => void;
  /** End an imperative drag. Settle behavior depends on the gesture:
   *
   * - Fling (|velocity| > ``FLING_VELOCITY_THRESHOLD``): step ONE
   *   detent in the fling's direction from ``startingDetent``.
   * - Slow release: must cross 25% of the way (in either direction)
   *   to the next detent to commit to it; otherwise snap back to
   *   ``startingDetent``.
   *
   * Positive velocity = finger moving up = sheet expanding.
   * ``startingDetent`` is the detent the user was at when they began
   * the gesture — required for the directional snap thresholds. */
  settleSheet: (
    currentFrac: number,
    velocityPxPerMs: number,
    startingDetent: BallotSheetDetent
  ) => void;
}

/** Per-frame drag value, split from the main context so a drag re-renders only
 * the sheet's animators (sheet, grabber, backdrop). */
interface BallotSheetLiveContextValue {
  /** Fraction (0..1) of max sheet height shown; tracks the finger mid-drag. */
  heightFrac: number;
}

const BallotSheetLiveContext = createContext<BallotSheetLiveContextValue>({ heightFrac: 0 });

const noopHandler = () => {};
const BallotSheetContext = createContext<BallotSheetContextValue>({
  detent: 'closed',
  ballotMode: 'national',
  locality: null,
  setLocality: noopHandler,
  activeTab: 'chat',
  setActiveTab: noopHandler,
  landingFullScreen: false,
  restingFrac: 0,
  restingSheetTopPx: 0,
  isDragging: false,
  maxHeightPx: 0,
  viewportHeightPx: 0,
  headerOffsetPx: DEFAULT_HEADER_OFFSET_PX,
  chatInputRect: null,
  setChatInputRect: noopHandler,
  keyboardUp: false,
  keyboardInsetPx: 0,
  open: noopHandler,
  close: noopHandler,
  setStatusPopoverOpen: noopHandler,
  isStatusPopoverActive: () => false,
  snapTo: noopHandler,
  pendingInstantSnap: false,
  setPreviewClosed: noopHandler,
  setPreviewTargetHeightPx: noopHandler,
  closedDetentFrac: 0,
  registerAddressInputFocuser: noopHandler,
  focusAddressInput: noopHandler,
  safeAreaBottomPx: 0,
  updateDragFrac: noopHandler,
  settleSheet: noopHandler,
});

interface BallotSheetProviderProps {
  children: React.ReactNode;
  /** Pixels of viewport reserved at the top for the app header.
   * Defaults to the bare toolbar height (48). Pass a larger value
   * (e.g., toolbar + notification banner) to keep the sheet's full
   * detent and the backdrop aligned with the actual app-header
   * bottom when extra chrome is present. */
  headerOffsetPx?: number;
  /** Data-source mode for the ballot. ``personal`` loads the signed-in
   * user's Cosmos data; ``national`` is the public snapshot. Set this
   * from auth state at the layout level so every consumer of the
   * sheet (page-level + in-chat) uses the same source. */
  ballotMode?: BallotSheetMode;
  /** Mobile navigation mode. ``'tab'`` renders the bottom tab bar + panel;
   * ``'sheet'`` the draggable sheet. Gates the tab-only side effects below so
   * they never fire while the sheet is in use. */
  navMode?: 'tab' | 'sheet';
  /** True when the ballot surface is showing the guest landing's address entry
   * instead of a ballot (no address anywhere yet, or the last saved ballot was
   * deleted). Drives ``landingFullScreen``. */
  guestLandingActive?: boolean;
}

export function BallotSheetProvider({
  children,
  headerOffsetPx = DEFAULT_HEADER_OFFSET_PX,
  ballotMode = 'national',
  navMode = 'sheet',
  guestLandingActive = false,
}: BallotSheetProviderProps) {
  const { isAuthenticated, user } = useAuth();
  const { track } = useAnalytics();

  const [detent, setDetent] = useState<BallotSheetDetent>('closed');
  // Locality label shown centered under the title in the sheet/grabber
  // header. Set by ``SampleBallotView`` (which computes it from the
  // signed-in user or guest address) so the collapsed band can show it.
  const [locality, setLocality] = useState<string | null>(null);
  // Tracks the previous detent so the analytics effect below can detect
  // closed → {mid, full} transitions specifically. ``useRef`` (not state)
  // because changing it shouldn't trigger an extra render.
  // ``isGuest`` and ``platform`` are stamped by the Mixpanel super
  // properties registrar in App.tsx — see MixpanelSuperPropertiesRegistrar.
  const prevDetentRef = useRef<BallotSheetDetent>('closed');
  useEffect(() => {
    const prev = prevDetentRef.current;
    if (prev === 'closed' && (detent === 'mid' || detent === 'full')) {
      track('Ballot Sheet Opened', { target: detent });
    }
    prevDetentRef.current = detent;
  }, [detent, track]);

  // --- Bottom tab bar (tab nav mode) ---
  // Which mobile screen the tab bar shows. Unused in sheet mode (the panel and
  // bar aren't rendered), and the side effects below are gated on ``navMode``.
  const [activeTab, setActiveTab] = useState<MobileBallotTab>('chat');
  const showChat = useCallback(() => setActiveTab('chat'), []);
  // Name & address signup (Auth0 done, ``initialSetupComplete`` still false):
  // there is no ballot yet, so park on Chat and let ``MobileTabBar`` hide the
  // Ballot / My Info tabs until the address is submitted.
  const signupAddressPending = Boolean(isAuthenticated && user && !user.initialSetupComplete);
  useEffect(() => {
    if (navMode !== 'tab' || !signupAddressPending) return;
    if (activeTab !== 'chat') setActiveTab('chat');
  }, [navMode, signupAddressPending, activeTab]);
  const onBallotTab = navMode === 'tab' && activeTab !== 'chat';
  // Guests only. ``guestLandingActive`` also covers a signed-in voter who
  // deleted their last ballot, and that voter keeps the app bar: it is their
  // only route to the menu, Settings and sign-out, and the landing's Sign In
  // button would be nonsense for an account they are already in.
  const landingFullScreen = onBallotTab && guestLandingActive && !isAuthenticated;

  // Analytics: keep the sheet-era funnel event firing on every Chat → ballot
  // transition in tab mode too, so existing Mixpanel reports stay continuous
  // across the two nav modes; the tab bar adds its own per-tap event on top.
  // ``isGuest`` and ``platform`` are stamped by the Mixpanel super properties
  // registrar in App.tsx.
  const prevTabRef = useRef<MobileBallotTab>('chat');
  useEffect(() => {
    const prev = prevTabRef.current;
    prevTabRef.current = activeTab;
    if (navMode !== 'tab') return;
    if (prev === 'chat' && activeTab !== 'chat') {
      track('Ballot Sheet Opened', { target: 'full', tab: activeTab });
    }
  }, [activeTab, navMode, track]);

  // Dismiss the keyboard when leaving the Chat tab (mobile app parity): the
  // composer keeps focus otherwise, and the tab bar stays hidden behind the
  // keyboard on the screen the user just switched to.
  useEffect(() => {
    if (!onBallotTab || typeof document === 'undefined') return;
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)
    ) {
      active.blur();
    }
  }, [onBallotTab]);

  // Browser back returns to the Chat tab before navigating away (mobile parity).
  // Push a history entry while on a ballot tab; pop it back on return to stay
  // balanced.
  useEffect(() => {
    if (typeof window === 'undefined' || !onBallotTab) return;
    window.history.pushState({ vmBallotSheet: true }, '');
    const onPopState = () => showChat();
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      if (window.history.state?.vmBallotSheet) window.history.back();
    };
  }, [onBallotTab, showChat]);

  // ``dragFrac`` is non-null only while a drag is in progress. Holds
  // the live finger-tracked height fraction (0..1). When the drag ends,
  // we set ``detent`` and clear this — CSS transitions take over.
  const [dragFrac, setDragFrac] = useState<number | null>(null);
  // Rect of the chat input the sheet anchors to when closed; ``null``
  // means no anchor (sheet sits at viewport bottom in full width).
  const [chatInputRect, setChatInputRect] = useState<ChatInputRect | null>(null);
  // ``true`` => the ``closed`` detent rests at ``PREVIEW_CLOSED_FRAC``
  // (~12%) instead of 0, leaving a small visible band of the sheet
  // even when the user "closes" it. Used by ChatWindow for guests on
  // the new chat page with no saved address — exposes the address
  // input as the persistent rest state.
  const [previewClosed, setPreviewClosed] = useState(false);
  // Measured at runtime by ``SampleBallotView`` (the distance from
  // the top of the sheet's teal header down to the bottom of the
  // address-input row). When non-null we derive the preview frac
  // from this px target so the address row lands exactly on the
  // sheet's bottom edge — no guessing.
  const [previewTargetHeightPx, setPreviewTargetHeightPx] = useState<number | null>(null);

  const [viewportHeight, setViewportHeight] = useState<number>(() =>
    typeof window === 'undefined' ? 0 : window.innerHeight
  );
  const [keyboardUp, setKeyboardUp] = useState<boolean>(false);
  const [keyboardInsetPx, setKeyboardInsetPx] = useState<number>(0);
  // Probed at runtime so we can add the home-indicator strip's height
  // back into the preview-closed sheet height (the SafeAreaFiller +
  // sheet's ``+ env(safe-area-inset-bottom)`` bottom shift effectively
  // shrink the sheet by this amount; bumping the frac compensates so
  // the preview view stays the intended height on devices with a
  // safe-area inset).
  const [safeAreaBottomPx, setSafeAreaBottomPx] = useState<number>(0);
  // Last non-zero safe area; the env probe reads 0 while the keyboard is up.
  const lastSafeAreaBottomRef = useRef(0);

  // Detect the on-screen keyboard so the sheet can sit above it and
  // consumers can keep their layout stable. Two paths:
  //
  //   1. ``navigator.virtualKeyboard`` (Chrome / Edge 108+, set to
  //      ``overlaysContent`` mode in ``index.tsx``). In overlay mode
  //      the visual + layout viewports both stay at their full
  //      pre-keyboard sizes, so the only authoritative keyboard
  //      signal is ``virtualKeyboard.boundingRect`` + the
  //      ``geometrychange`` event.
  //   2. ``window.visualViewport`` fallback (iOS Safari, older
  //      Chromium). Here ``visualViewport.height`` shrinks above the
  //      keyboard and the delta from ``window.innerHeight`` gives
  //      the keyboard inset.
  //
  // ``viewportHeightPx`` becomes the "available height above the
  // keyboard" in both modes — downstream geometry math
  // (``BallotSheet`` ``closedTopY``) doesn't have to know which path
  // was used.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // @ts-expect-error VirtualKeyboard API not in default TS lib.
    const vk: VirtualKeyboard | undefined = navigator.virtualKeyboard;
    const useVk = !!vk;

    // Probe for ``env(safe-area-inset-bottom)`` — there's no direct
    // JS API. Render a hidden 1px div with padding-bottom set to
    // ``env(safe-area-inset-bottom)`` and read back the computed
    // pixel value. Done once for the lifetime of the provider; we
    // re-read on every ``updateHeight`` in case the OS shifts the
    // safe area dynamically.
    const safeAreaProbe = document.createElement('div');
    safeAreaProbe.style.cssText =
      'position:fixed;visibility:hidden;pointer-events:none;width:1px;height:1px;padding-bottom:env(safe-area-inset-bottom);box-sizing:content-box;';
    document.body.appendChild(safeAreaProbe);

    const updateHeight = () => {
      // Read safe-area-inset-bottom in CSS px via the probe so we
      // can fold it back into the preview-closed sheet height.
      const safeAreaProbed = parseFloat(getComputedStyle(safeAreaProbe).paddingBottom) || 0;
      setSafeAreaBottomPx(safeAreaProbed);
      if (safeAreaProbed > 0) lastSafeAreaBottomRef.current = safeAreaProbed;

      // Pinch-zoom (Safari especially) shrinks ``visualViewport
      // .height`` in CSS px while ``scale > 1``. That isn't a
      // layout change — the page is the same size, the user is just
      // looking at it closer. Skipping the update during zoom
      // prevents the sheet / grabber from resizing while the user
      // pinches in / out.
      const scale = window.visualViewport?.scale ?? 1;
      if (scale > 1.01) return;
      // On Safari iOS, ``visualViewport.offsetTop`` is non-zero when
      // the browser scrolls the visual viewport up to reveal a
      // focused input above the keyboard. We subtract it from the
      // inset calc below so the keyboard-inset variable reflects
      // only the keyboard's height, not the keyboard plus the vv
      // shift. We don't counter-translate ``position: fixed`` top
      // elements — Safari's natural "page scrolls under the app
      // bar when keyboard opens" behavior is the desired UX.
      const offsetTop = window.visualViewport?.offsetTop ?? 0;
      const ih = window.innerHeight;
      let inset = 0;
      if (useVk) {
        // ``boundingRect`` is zero-area when the keyboard is closed.
        // @ts-expect-error see above.
        inset = vk?.boundingRect?.height ?? 0;
      } else {
        const vv = window.visualViewport?.height;
        // Subtract ``offsetTop`` too so the inset reflects only the
        // keyboard's layout-px height, not the keyboard plus any
        // Safari visual-viewport shift. Without this, ``inset``
        // double-counts: the sheet's bottom anchor ends up
        // ``offsetTop`` too high, landing the grabber near the
        // middle of the page instead of just above the keyboard.
        inset = vv ? Math.max(0, ih - vv - offsetTop) : 0;
      }
      const keyboardOpen = inset > 80;
      // Edge Android quirk: ``vk.boundingRect.height`` (and
      // ``env(keyboard-inset-height)``) report the raw keyboard
      // height, but a ``position: fixed`` element's CSS ``bottom``
      // anchors to the layout viewport — which on Edge stops ~53 px
      // above the keyboard top (the persistent bottom browser
      // toolbar). So anchoring against the raw inset lands ~53 px
      // ABOVE the keyboard's actual top edge. Subtract here, at the
      // SOURCE — every downstream consumer (chat input pb, bar
      // bottom, BallotSheet positioning) reads the corrected value
      // automatically without each needing its own Edge awareness.
      // Chrome / other Chromium / iOS Safari: ``EDGE_BOTTOM_TOOLBAR_PX``
      // resolves to 0, no-op.
      const EDGE_BOTTOM_TOOLBAR_PX = 53;
      const isEdgeAndroid = typeof navigator !== 'undefined' && /EdgA\//.test(navigator.userAgent);
      const edgeOvershoot = isEdgeAndroid && keyboardOpen ? EDGE_BOTTOM_TOOLBAR_PX : 0;
      // Chrome's keyboard sits above the gesture bar (env reads 0 while up, so use
      // the remembered value); Edge already measures to the bottom, so exclude it.
      const kbAboveSafeArea =
        useVk && keyboardOpen && !isEdgeAndroid ? lastSafeAreaBottomRef.current : 0;
      const effectiveInset = Math.max(0, inset - edgeOvershoot) + kbAboveSafeArea;
      setKeyboardUp(keyboardOpen);
      setKeyboardInsetPx(keyboardOpen ? effectiveInset : 0);
      const above = keyboardOpen ? ih - effectiveInset : ih;
      setViewportHeight(above);
      // Expose the (overshoot-corrected) keyboard inset as a CSS
      // variable so layout consumers (chat input pb, address bar
      // bottom, ChatLandingState) can shift content above the
      // keyboard via a single ``var(--keyboard-inset-px)`` reference,
      // with no per-consumer Edge handling required.
      document.documentElement.style.setProperty(
        '--keyboard-inset-px',
        `${keyboardOpen ? effectiveInset : 0}px`
      );
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    window.visualViewport?.addEventListener('resize', updateHeight);
    // ``scroll`` on visualViewport fires when ``offsetTop`` changes
    // — that's how Safari signals the keyboard-induced visual-
    // viewport shift. Without listening here we'd miss the offset
    // updates and the ``--visual-viewport-offset-top`` CSS variable
    // would stay stale, leaving the top app bar out of view.
    window.visualViewport?.addEventListener('scroll', updateHeight);
    if (useVk) {
      // @ts-expect-error see above.
      vk?.addEventListener?.('geometrychange', updateHeight);
    }
    return () => {
      window.removeEventListener('resize', updateHeight);
      window.visualViewport?.removeEventListener('resize', updateHeight);
      window.visualViewport?.removeEventListener('scroll', updateHeight);
      if (useVk) {
        // @ts-expect-error see above.
        vk?.removeEventListener?.('geometrychange', updateHeight);
      }
      document.body.removeChild(safeAreaProbe);
    };
  }, []);

  const maxHeightPx = Math.max(0, viewportHeight - headerOffsetPx);

  // Effective detent fractions for this render. ``closed`` lifts to
  // a fraction that produces a CONSISTENT visible sheet height
  // (above the safe-area filler) across devices when
  // ``previewClosed`` is on.
  //
  // Math: the sheet's height above the filler at frac ``F`` is
  //
  //   ``H = SHEET_HEADER_PX + actualTravel * F``
  //
  // where ``actualTravel = viewportHeight - SHEET_HEADER - safe-area
  // - headerOffset`` (the px distance the sheet's top edge moves
  // from closed to full on THIS device). On a safe-area device the
  // travel is shorter, so the SAME ``F`` would give a SHORTER ``H``.
  // We compute a target ``H`` from ``PREVIEW_CLOSED_FRAC`` against a
  // BASELINE travel (safe-area = 0), then back-solve for the frac
  // each device needs to hit that ``H``.
  const detentFracs = useMemo<Record<BallotSheetDetent, number>>(() => {
    // Mirrors ``HEADER_HEIGHT_PX`` in ``BallotSheet`` /
    // ``BallotGrabber``. Inlined to avoid a cyclic import; keep in
    // sync if either component's value changes.
    const SHEET_HEADER_PX = 56;
    const baselineTravelPx = Math.max(1, viewportHeight - SHEET_HEADER_PX - headerOffsetPx);
    const actualTravelPx = Math.max(1, baselineTravelPx - safeAreaBottomPx);
    // Prefer the measured target height when available — that's the
    // address-row's bottom relative to the sheet's top, plus the
    // safe-area inset so the row's bottom edge lands exactly on the
    // top of the SafeAreaFiller. Falls back to the static fraction
    // (``PREVIEW_CLOSED_FRAC``) until SampleBallotView reports a
    // measurement.
    const targetVisibleHeightPx =
      previewTargetHeightPx != null
        ? previewTargetHeightPx
        : SHEET_HEADER_PX + baselineTravelPx * PREVIEW_CLOSED_FRAC;
    const previewFrac = Math.min(
      1,
      Math.max(0, (targetVisibleHeightPx - SHEET_HEADER_PX) / actualTravelPx)
    );
    return {
      ...DETENT_FRACS_BASE,
      closed: previewClosed ? previewFrac : DEFAULT_CLOSED_FRAC,
    };
  }, [previewClosed, safeAreaBottomPx, viewportHeight, headerOffsetPx, previewTargetHeightPx]);

  const restingFrac = detentFracs[detent];
  const heightFrac = dragFrac ?? restingFrac;
  const isDragging = dragFrac !== null;

  // Sheet top-edge Y at the settled detent (mirrors BallotSheet's lerp; 56 = header).
  const restingClosedTopPx = chatInputRect
    ? chatInputRect.top - 56
    : viewportHeight - 56 - safeAreaBottomPx;
  const restingSheetTopPx =
    restingClosedTopPx + (headerOffsetPx - restingClosedTopPx) * restingFrac;

  // ``true`` for exactly one render cycle after a ``snapTo({ instant:
  // true })`` call. BallotSheet reads this and suppresses its top /
  // bottom transition for that render so the sheet jumps to the new
  // detent without a 280ms animation. Required when the snap is
  // triggered by user input that will open the keyboard immediately
  // (Safari iOS otherwise scrolls the layout based on the pre-snap
  // position of the focused input, displacing the whole sheet).
  const [pendingInstantSnap, setPendingInstantSnap] = useState(false);
  useEffect(() => {
    if (!pendingInstantSnap) return undefined;
    // Clear on the next macrotask so the consumer has rendered with
    // the suppressed transition before we restore normal animation
    // behavior for any subsequent state change.
    const id = setTimeout(() => setPendingInstantSnap(false), 0);
    return () => clearTimeout(id);
  }, [pendingInstantSnap]);

  // MobileAddressBar registers a focus callback on mount so external
  // consumers (LeftPaneMenu's "My Ballot Preview" in the no-address
  // scenario) can request that the address input grab focus without
  // reaching into the bar's DOM directly. Stored in a ref so
  // registration doesn't re-render every context consumer.
  const addressInputFocuserRef = useRef<(() => void) | null>(null);
  const registerAddressInputFocuser = useCallback((fn: (() => void) | null) => {
    addressInputFocuserRef.current = fn;
  }, []);
  const focusAddressInput = useCallback(() => {
    addressInputFocuserRef.current?.();
  }, []);

  const snapTo = useCallback((target: BallotSheetDetent, opts?: { instant?: boolean }) => {
    if (opts?.instant) setPendingInstantSnap(true);
    setDetent(target);
    setDragFrac(null);
  }, []);

  const doOpen = useCallback(() => {
    setDetent((prev) => (prev === 'closed' ? 'mid' : prev));
    setDragFrac(null);
  }, []);

  const pendingOpenRef = useRef(false);
  const open = useCallback(() => {
    // Keyboard up: dismiss it and defer the open until the viewport settles, so
    // the sheet lands straight on the keyboard-down mid (no 0.6 transient).
    if (keyboardUp && typeof document !== 'undefined') {
      pendingOpenRef.current = true;
      const active = document.activeElement;
      if (active instanceof HTMLElement) active.blur();
      window.setTimeout(() => {
        if (!pendingOpenRef.current) return;
        pendingOpenRef.current = false;
        doOpen();
      }, 400); // fallback if the viewport-settle never fires
      return;
    }
    doOpen();
  }, [keyboardUp, doOpen]);

  // Finish a keyboard-deferred open once the keyboard has closed.
  useEffect(() => {
    if (keyboardUp || !pendingOpenRef.current) return;
    pendingOpenRef.current = false;
    doOpen();
  }, [keyboardUp, doOpen]);

  const close = useCallback(() => snapTo('closed'), [snapTo]);

  // Dismiss the keyboard when the ballot opens (mobile parity). Keyed on open, not
  // the live drag: blurring mid-drag shifts the anchor and yanks the sheet.
  const sheetOpen = detent !== 'closed';
  useEffect(() => {
    if (!sheetOpen || typeof document === 'undefined') return;
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)
    ) {
      active.blur();
    }
  }, [sheetOpen]);

  // Browser back collapses an open sheet before navigating away (mobile parity).
  // Push a history entry while open; pop it back on close to stay balanced.
  const isOpen = detent !== 'closed';
  useEffect(() => {
    if (typeof window === 'undefined' || !isOpen) return;
    window.history.pushState({ vmBallotSheet: true }, '');
    const onPopState = () => close();
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      if (window.history.state?.vmBallotSheet) window.history.back();
    };
  }, [isOpen, close]);

  // Lets the header skip opening the sheet on a tap that's dismissing the
  // status popover (its Popover backdrop doesn't intercept on mobile).
  const statusPopoverActiveRef = useRef(false);
  const statusPopoverClosedAtRef = useRef(0);
  const setStatusPopoverOpen = useCallback((isOpen: boolean) => {
    statusPopoverActiveRef.current = isOpen;
    if (!isOpen) statusPopoverClosedAtRef.current = performance.now();
  }, []);
  const isStatusPopoverActive = useCallback(
    () =>
      statusPopoverActiveRef.current || performance.now() - statusPopoverClosedAtRef.current < 300,
    []
  );

  // Auto-close on sign-out — matches the mobile app's behavior so a
  // signed-out user doesn't see stale ballot content. Triggered only
  // on the transition ``authenticated → unauthenticated`` so a guest
  // who arrives at the app already unauthenticated doesn't get the
  // sheet forced closed on every re-render (which would clobber a
  // ``snapTo('full')`` set by the guest landing's "See my ballot"
  // button, among other intent-driven detent changes).
  const wasAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => {
    if (wasAuthenticatedRef.current && !isAuthenticated) {
      // Tab nav mode returns to the Chat tab; sheet mode closes the sheet.
      if (navMode === 'tab') showChat();
      else close();
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated, close, navMode, showChat]);

  // Drag (non-fling) walks the detent ladder so a deliberate long
  // drag can skip past intermediate detents — e.g., dragging from
  // ``full`` all the way down past ``mid`` snaps to ``closed`` rather
  // than catching at ``mid``. Each step requires crossing the 25%
  // threshold of its own segment. Fling stays single-step so a flick
  // doesn't accidentally jump two detents.
  const settleSheet = useCallback(
    (currentFrac: number, v: number, startingDetent: BallotSheetDetent) => {
      let target: BallotSheetDetent;

      if (v > FLING_VELOCITY_THRESHOLD) {
        // Fast upward fling → step one detent up.
        target = adjacentDetent(startingDetent, 'up');
      } else if (v < -FLING_VELOCITY_THRESHOLD) {
        // Fast downward fling → step one detent down.
        target = adjacentDetent(startingDetent, 'down');
      } else {
        // Slow release — walk detents in the drag's direction, taking
        // the furthest one whose threshold the user has crossed.
        const startFrac = detentFracs[startingDetent];
        const startIdx = DETENT_ORDER.indexOf(startingDetent);
        target = startingDetent;

        if (currentFrac > startFrac) {
          let prevFrac = startFrac;
          for (let i = startIdx + 1; i < DETENT_ORDER.length; i += 1) {
            const next = DETENT_ORDER[i];
            const nextFrac = detentFracs[next];
            const threshold = prevFrac + (nextFrac - prevFrac) * SNAP_UP_THRESHOLD_FRAC;
            if (currentFrac < threshold) break;
            target = next;
            prevFrac = nextFrac;
          }
        } else if (currentFrac < startFrac) {
          let prevFrac = startFrac;
          for (let i = startIdx - 1; i >= 0; i -= 1) {
            const next = DETENT_ORDER[i];
            const nextFrac = detentFracs[next];
            const threshold = prevFrac - (prevFrac - nextFrac) * SNAP_DOWN_THRESHOLD_FRAC;
            if (currentFrac > threshold) break;
            target = next;
            prevFrac = nextFrac;
          }
        }
      }

      snapTo(target);
    },
    [snapTo, detentFracs]
  );

  const updateDragFrac = useCallback((frac: number) => {
    setDragFrac(Math.max(0, Math.min(1, frac)));
  }, []);

  const value = useMemo<BallotSheetContextValue>(
    () => ({
      detent,
      ballotMode,
      locality,
      setLocality,
      activeTab,
      setActiveTab,
      landingFullScreen,
      restingFrac,
      restingSheetTopPx,
      isDragging,
      maxHeightPx,
      viewportHeightPx: viewportHeight,
      headerOffsetPx,
      chatInputRect,
      setChatInputRect,
      keyboardUp,
      keyboardInsetPx,
      open,
      close,
      setStatusPopoverOpen,
      isStatusPopoverActive,
      snapTo,
      pendingInstantSnap,
      setPreviewClosed,
      setPreviewTargetHeightPx,
      closedDetentFrac: detentFracs.closed,
      registerAddressInputFocuser,
      focusAddressInput,
      safeAreaBottomPx,
      updateDragFrac,
      settleSheet,
    }),
    [
      detent,
      ballotMode,
      locality,
      activeTab,
      landingFullScreen,
      restingFrac,
      restingSheetTopPx,
      isDragging,
      maxHeightPx,
      viewportHeight,
      headerOffsetPx,
      chatInputRect,
      keyboardUp,
      keyboardInsetPx,
      open,
      close,
      setStatusPopoverOpen,
      isStatusPopoverActive,
      snapTo,
      pendingInstantSnap,
      setPreviewClosed,
      setPreviewTargetHeightPx,
      detentFracs,
      registerAddressInputFocuser,
      focusAddressInput,
      safeAreaBottomPx,
      updateDragFrac,
      settleSheet,
    ]
  );

  // Isolated so per-frame drag updates re-render only live consumers.
  const liveValue = useMemo<BallotSheetLiveContextValue>(() => ({ heightFrac }), [heightFrac]);

  return (
    <BallotSheetContext.Provider value={value}>
      <BallotSheetLiveContext.Provider value={liveValue}>
        {children}
      </BallotSheetLiveContext.Provider>
    </BallotSheetContext.Provider>
  );
}

export function useBallotSheet() {
  return useContext(BallotSheetContext);
}

/** Live per-frame drag fraction — sheet animators only; others read ``restingFrac``. */
export function useBallotSheetLive() {
  return useContext(BallotSheetLiveContext);
}
